import { Web as SIPWeb } from 'sip.js'

class VoiceCallManager {
  constructor(opts = {}) {
    this.serverUrl = opts.serverUrl
    this.username = opts.username
    this.password = opts.password
    this.realm = opts.realm || 'asterisk'
    this.targetUri = opts.targetUri
    this.fromUri = opts.fromUri
    this.sessionId = opts.sessionId
    this.iceServers = opts.iceServers || [{ urls: 'stun:stun.l.google.com:19302' }]
    this.onStateChange = opts.onStateChange || (() => {})
    this.onCallStart = opts.onCallStart || (() => {})
    this.onCallEnd = opts.onCallEnd || (() => {})
    this.onError = opts.onError || (() => {})
    this.onSipMessage = opts.onSipMessage || (() => {})

    this.simple = null
    this._state = 'idle'
    this._destroyed = false
    this._callInitiated = false
    this._wakeLock = null
  }

  async _requestWakeLock() {
    try {
      if ('wakeLock' in navigator) {
        this._wakeLock = await navigator.wakeLock.request('screen')
        this._wakeLock.addEventListener('release', () => {})
      }
    } catch (e) {
      /* Wake Lock not supported or denied */
    }
  }

  _releaseWakeLock() {
    if (this._wakeLock) {
      try { this._wakeLock.release() } catch (e) { /* ignore */ }
      this._wakeLock = null
    }
  }

  _onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && this._state === 'connected') {
      this._requestWakeLock()
    }
  }

  async start() {
    this._setState('connecting')

    const fullWsUrl = this.serverUrl.startsWith('ws') || this.serverUrl.startsWith('http')
      ? this.serverUrl
      : (window.location.protocol === 'https:' ? 'wss:' : 'ws:') + '//' + window.location.host + this.serverUrl
    const hostname = this._extractHostname(fullWsUrl)
    const remoteAudio = new Audio()
    remoteAudio.autoplay = true

    this._patchGetUserMedia()

    const hasTurn = this.iceServers.some(function(s) {
      const u = Array.isArray(s.urls) ? s.urls[0] : s.urls
      return u && (u.startsWith('turn:') || u.startsWith('turns:'))
    })

    this.simple = new SIPWeb.Simple({
      media: {
        remote: {
          audio: remoteAudio,
        },
      },
      ua: {
        uri: 'sip:' + this.username + '@' + hostname,
        authorizationUser: this.username,
        password: this.password,
        displayName: this.username,
        wsServers: fullWsUrl,
        traceSip: false,
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionOptions: {
            iceCheckingTimeout: 15000,
            rtcConfiguration: {
              iceServers: this.iceServers,
              iceTransportPolicy: hasTurn ? 'relay' : 'all',
            },
          },
        },
      },
    })

    this._setupSimpleListeners()
  }

  _patchGetUserMedia() {
    if (navigator.mediaDevices.__patched) return
    navigator.mediaDevices.__patched = true
    const original = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices)
    let silentStream
    navigator.mediaDevices.getUserMedia = async (constraints) => {
      try {
        return await original(constraints)
      } catch (err) {
        if (!silentStream) {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)()
            const dst = ctx.createMediaStreamDestination()
            const osc = ctx.createOscillator()
            osc.frequency.value = 0
            osc.connect(dst)
            osc.start()
            silentStream = dst.stream
          } catch (e2) {
            silentStream = new MediaStream()
          }
        }
        return silentStream
      }
    }
  }

  _extractHostname(urlStr) {
    try {
      const u = new URL(urlStr)
      if (u.hostname) return u.hostname
    } catch (e) { /* fall through */ }
    try {
      return window.location.hostname
    } catch (e) {
      return '127.0.0.1'
    }
  }

  _setupSimpleListeners() {
    this.simple.on('registered', () => {
      if (this._destroyed || this._callInitiated) return
      this._setState('ringing')

      const target = this.targetUri || 'sip:AI-' + (this.sessionId || 'unknown')
      const destination = target.startsWith('sip:') ? target : 'sip:' + target
      this._callInitiated = true
      try {
        this.simple.call(destination)
      } catch (err) {
        this._error('Call initiation failed: ' + (err.message || err))
      }
    })

    this.simple.on('ringing', () => {
      if (this._destroyed) return
      this._setState('ringing')
    })

    this.simple.on('connected', () => {
      if (this._destroyed) return
      this._setState('connected')
      this._requestWakeLock()
      document.addEventListener('visibilitychange', this._onVisibilityChange)
      if (this.onCallStart) this.onCallStart({})
    })

    this.simple.on('ended', () => {
      if (this._destroyed) return
      this._cleanup()
    })

    this.simple.on('failed', () => {
      if (this._destroyed) return
      this._error('Call failed')
    })

    this.simple.on('registrationFailed', () => {
      if (this._destroyed) return
      this._error('SIP registration failed')
    })
  }

  hangup() {
    if (this.simple) {
      try { this.simple.hangup() } catch (e) { /* ignore */ }
    }
    this._cleanup()
    this._setState('idle')
  }

  _setState(s) {
    this._state = s
    if (this.onStateChange) this.onStateChange(s)
  }

  _cleanup() {
    this._destroyed = true
    document.removeEventListener('visibilitychange', this._onVisibilityChange)
    this._releaseWakeLock()
    if (this.simple) {
      try {
        this.simple.removeAllListeners()
        if (this.simple.state !== SIPWeb.Simple.C.STATUS_COMPLETED) {
          try { this.simple.hangup() } catch (e) { /* ignore */ }
        }
      } catch (e) { /* ignore */ }
      this.simple = null
    }
    if (this.onCallEnd) this.onCallEnd({})
  }

  _error(msg) {
    if (this.onError) this.onError(msg)
    this._cleanup()
    this._setState('error')
  }
}

export default VoiceCallManager
